/**
 * Layout Component
 *
 * Main layout wrapper with navigation header.
 * Provides consistent structure across all pages.
 * Uses sticky nav with backdrop blur effect matching design spec.
 * On mobile, uses a hamburger menu to prevent overflow.
 */

import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  HeartHandshake,
  User,
  LogOut,
  Headphones,
  LineChart,
  Menu,
  LayoutDashboard,
  BookOpen,
  Users,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { useAuth } from "../hooks/useAuth";
import { trackEvent } from "../lib/analytics";
import { TrialBanner } from "./TrialBanner";
import { InvitationBadge } from "./InvitationBadge";
import { OverdueGrowthBadge } from "./OverdueGrowthBadge";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { isAuthenticated } = useAuthStatus();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    void trackEvent("user.logged_out"); // fire-and-forget
    logout();
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link
            to={isAuthenticated ? "/dashboard" : "/"}
            className="flex items-center gap-2"
          >
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </Link>
          {isAuthenticated && (
            <>
              {/* Desktop navigation */}
              <div className="hidden md:flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate("/dashboard")}>
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/reflections")}
                >
                  Reflections
                </Button>
                <Button variant="ghost" onClick={() => navigate("/partners")}>
                  Partners
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/growth")}
                  className="gap-1"
                >
                  <Sparkles className="w-4 h-4" />
                  Growth
                  <OverdueGrowthBadge />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/tracker")}
                  className="gap-1"
                >
                  <LineChart className="w-4 h-4" />
                  Tracker
                </Button>
                <InvitationBadge />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/support")}
                  title="Support"
                >
                  <Headphones className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/account")}
                  title="Account"
                >
                  <User className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>

              {/* Mobile hamburger menu */}
              <div
                className="md:hidden flex items-center gap-2"
                data-testid="mobile-nav"
              >
                <InvitationBadge />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard")}
                      className={
                        isActive("/dashboard") ? "bg-accent font-medium" : ""
                      }
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/reflections")}
                      className={
                        isActive("/reflections") ? "bg-accent font-medium" : ""
                      }
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Reflections
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/partners")}
                      className={
                        isActive("/partners") ? "bg-accent font-medium" : ""
                      }
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Partners
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/growth")}
                      className={
                        isActive("/growth") ? "bg-accent font-medium" : ""
                      }
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Growth
                      <OverdueGrowthBadge className="ml-auto" />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/tracker")}
                      className={
                        isActive("/tracker") ? "bg-accent font-medium" : ""
                      }
                    >
                      <LineChart className="w-4 h-4 mr-2" />
                      Tracker
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/support")}>
                      <Headphones className="w-4 h-4 mr-2" />
                      Support
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/account")}>
                      <User className="w-4 h-4 mr-2" />
                      Account
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )}
          {!isAuthenticated && (
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/")}>
                Sign In
              </Button>
              <Button onClick={() => navigate("/")}>Get Started</Button>
            </div>
          )}
        </div>
      </nav>
      {isAuthenticated && <TrialBanner />}
      <main>{children}</main>
    </div>
  );
}
